import { FormGroup, ITreeNode, NonIdealState, Slider, Tree } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import isEmpty from "lodash/isEmpty";
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";

import * as editActions from "core/actions/editActions";
import initialState from "core/reducers/initialState";
import ILocalCacheService from "core/service/cache/ILocalCacheService";
import ServiceRegistry from "core/util/ServiceRegistry";
import { EditorConfig } from "./EditorConfigForm";

/**
 * renders a tree view of panels for a particular layout (string array).
 *
 * @author chris
 */

interface ILayoutTreeStateProps {
	active?: null | editActions.IActiveArgs;
	layout: string[];
	panels: { [instance: string]: any; };
}

interface ILayoutDispatchProps {
	actions: typeof editActions;
}

interface ILayoutTreeOwnProps {
	// layout?: string[];
	visible?: string[];
}

type ResultProps = ILayoutTreeStateProps & ILayoutDispatchProps & ILayoutTreeOwnProps;

function mapStateToProps(state: typeof initialState = initialState, ownProps: ILayoutTreeOwnProps): ILayoutTreeStateProps {

	return {
		panels: state.config.panels,
		active: state.edit.active,
		layout: state.layout
	};
}

function mapDispatchToProps(dispatch: Dispatch<typeof initialState>) {

	return {
		actions: bindActionCreators(editActions, dispatch)
	};
}

interface ILayoutState {
	expanded: { [instance: string]: boolean; };
	minExpandedDepth: number;
}

class LayoutTree extends React.PureComponent<ResultProps, ILayoutState> {

	public static ICONS = {
		container: IconNames.FOLDER_CLOSE,
		layout: IconNames.COLUMN_LAYOUT,
		page: IconNames.DOCUMENT
	};

	public constructor(props: ResultProps) {
		super(props);

		const editorConfig = ServiceRegistry.get(ILocalCacheService).getAs("config", EditorConfig, "editor");

		this.state = { minExpandedDepth: editorConfig.minLayoutTreeDepth || 1, expanded: {} };
	}

	public render() {

		const nodes = this.props.layout && !isEmpty(this.props.panels) ? this.props.layout.map(this.getTreeNode).filter((n) => n !== null) : null;

		if (!nodes) {
			return (
				<NonIdealState
					icon={IconNames.WRENCH}
					title="No Layout Selected"
					description="Choose a layout to view tree structure."
				/>
			);
		}

		return (
			<div>
				<FormGroup label="Auto Expand">
					<Slider
						min={0}
						max={6}
						stepSize={1}
						onRelease={this.onDepthRelease}
						onChange={this.onChangeMinDepth}
						labelStepSize={1}
						value={this.state.minExpandedDepth}
					/>
				</FormGroup>
				<Tree
					contents={nodes}
					onNodeClick={this.onNodeClick}
					onNodeCollapse={this.onNodeCollapse}
					onNodeExpand={this.onNodeExpand}
				/>
			</div>
		);
	}

	private onNodeClick = (node: ITreeNode<string>) => {
		this.props.actions.editSetActive((this.props.active && this.props.active.instance === node.nodeData) ? null : {
			type: "panel",
			instance: node.nodeData
		});
	}

	private onNodeCollapse = (node: ITreeNode<string>) => {
		this.setState({ expanded: { ...this.state.expanded, [node.nodeData]: false } });
	}

	private onNodeExpand = (node: ITreeNode<string>) => {
		this.setState({ expanded: { ...this.state.expanded, [node.nodeData]: true } });
	}

	private getTreeNode = (instance: string, depth: number = 0): ITreeNode | null => {

		const panel = this.props.panels[instance];

		if (!panel) {
			return null;
		}

		const childIds = this.getChildInstances(panel);

		const hasChildren = childIds.length > 0;

		return {
			id: instance,
			label: panel["__editName"] || panel["editName"] || "Untitled",
			icon: LayoutTree.ICONS[panel.id],
			isExpanded: this.state.expanded[instance] || (depth < this.state.minExpandedDepth),
			isSelected: (this.props.active && this.props.active.instance === instance),
			hasCaret: hasChildren && depth >= this.state.minExpandedDepth,
			nodeData: instance,
			childNodes: hasChildren && childIds.map((ci) => this.getTreeNode(ci, depth + 1)).filter((node) => !!node) || null
		};
	}

	private getChildInstances(panel: any): string[] {
		const instances = [];

		if (panel.children) {
			panel.children.forEach((inst: string) => instances.push(inst));
		}

		if (panel.layout && panel.layout.columns) {
			panel.layout.columns.forEach(({containerInstance}) => instances.push(containerInstance));
		}

		return instances;
	}

	private onDepthRelease = (minExpandedDepth: number) => {
		const cacheService = ServiceRegistry.get(ILocalCacheService);
		const c = cacheService.getAs("config", EditorConfig, "editor");
		c.minLayoutTreeDepth = minExpandedDepth;
		cacheService.put("config", c, "editor");
		this.onChangeMinDepth(minExpandedDepth);
	}

	private onChangeMinDepth = (minExpandedDepth: number) => {
		this.setState({minExpandedDepth});
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(LayoutTree);



// WEBPACK FOOTER //
// ./src/core/components/editor/LayoutTree.tsx